import { createContext, useContext, type ReactNode } from "react";

import { useRouter } from "next/router";

import { School } from "@/types/models/School.interface";
import { User } from "@/types/models/User.interface";
import useUser from "@/users/useUser";
import { getSelectedQueryValue } from "@/reports/utils/getSelectedQueryValue";

import {
  IS_INCLUSIVE_BETW_JULY1_TO_SEPT_15,
  START_OF_45_DAYS,
  START_OF_60_DAYS,
  START_OF_90_DAYS,
  TODAY,
} from "@/reports/constants";
import getMinimumDateForReports from "@/reports/utils/getMinimumDateForReports";
import {
  getMinMaxDates,
  getSelectedDateText,
  type DateRangeTranslatedValues,
} from "../AdminInsightsReportFilters/DateRangeFilter/DateRangeFilter.utils";

interface Filters {
  school: string | undefined;
  grade: string | undefined;
  dateRange: [string, string];
  searchQuery: string | undefined;
}

const initialContext: FiltersContextInterface = {
  selectedFilters: {
    school: undefined,
    grade: undefined,
    dateRange: ["", ""],
    dateFilterLabel: "",
    dateCopy: "",
    searchQuery: undefined,
  },
  user: {} as unknown as User,
  schools: [] as unknown as School[],
  dateRangeMenuOptions: [{ value: "", label: "" }],
  dateRangeFilterDefaultOption: { value: "", label: "" },
};

const AdminReportingFiltersContext = createContext(initialContext);

export const AdminReportingFiltersContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { query } = useRouter();
  const { data: user }: { data: User; loading: boolean } = useUser();

  const schools = user?.schools?.sort((aSchool, bSchool) =>
    aSchool.name.localeCompare(bSchool.name),
  );

  const selectedSchool = getSelectedQueryValue(query["school"]);
  const selectedGrade = getSelectedQueryValue(query["grade"]);
  const selectedDateRange = getSelectedQueryValue(query["date_range"]);
  const searchQuery = getSelectedQueryValue(query["search_query"]);
  const selectedMinDate = getSelectedQueryValue(query["min_date"]);
  const selectedMaxDate = getSelectedQueryValue(query["max_date"]);

  const dateRangeTranslatedValues: DateRangeTranslatedValues = [
    {
      value: "july_to_date",
      min: getMinimumDateForReports(START_OF_45_DAYS),
      max: TODAY,
      label: "July to date",
      copy: "Since July",
    },
    {
      value: "90_days",
      min: START_OF_90_DAYS,
      max: TODAY,
      label: "Last 90 days",
      copy: "In the last 90 days",
    },
    {
      value: "60_days",
      min: START_OF_60_DAYS,
      max: TODAY,
      label: "Last 60 days",
      copy: "In the last 60 days",
    },
    {
      value: "45_days",
      min: START_OF_45_DAYS,
      max: TODAY,
      label: "Last 45 days",
      copy: "In the last 45 days",
    },
  ];

  const { filterLabel, copy } = getSelectedDateText(
    selectedDateRange,
    dateRangeTranslatedValues,
  );
  const selectedFilters = {
    school: selectedSchool,
    grade: selectedGrade,
    dateRange: getMinMaxDates(
      selectedDateRange,
      dateRangeTranslatedValues,
      selectedMinDate,
      selectedMaxDate,
    ),
    dateFilterLabel: filterLabel,
    dateCopy: copy,
    searchQuery,
  };

  const dateRangeMenuOptions = dateRangeTranslatedValues.map((dateRange) => {
    return { value: dateRange.value, label: dateRange.label };
  });

  let dateRangeFilterDefaultOption = {
    value: "july_to_date",
    label: "July to date",
  };

  if (IS_INCLUSIVE_BETW_JULY1_TO_SEPT_15) {
    dateRangeFilterDefaultOption = { value: "45_days", label: "Last 45 days" };
    // remove july to date option
    dateRangeMenuOptions.shift();
  }

  return (
    <AdminReportingFiltersContext.Provider
      value={{
        selectedFilters,
        user,
        schools,
        dateRangeMenuOptions,
        dateRangeFilterDefaultOption,
      }}
    >
      {children}
    </AdminReportingFiltersContext.Provider>
  );
};

export interface FiltersContextInterface {
  selectedFilters: Filters & {
    dateFilterLabel: string;
    dateCopy?: string | undefined;
  };
  user: User;
  schools: School[];
  dateRangeMenuOptions: Array<{ value: string; label: string }>;
  dateRangeFilterDefaultOption: { value: string; label: string };
}

const useAdminReportingFiltersContext = (): FiltersContextInterface => {
  const adminReportingFiltersContext = useContext(AdminReportingFiltersContext);

  if (adminReportingFiltersContext === undefined) {
    throw new Error(
      "adminReportingFiltersContext must be used within an AdminReportingFiltersContextProvider.",
    );
  }

  return adminReportingFiltersContext;
};

export default useAdminReportingFiltersContext;
